<template>
  <QuestionPadding data-test-id="OpenTextQuestion">
    <HintText>{{ c('text_response.start_typing') }}</HintText>
    <ScrollIntersectionObserver>
      <div v-if="invalidAnswerIds.length > 0" class="open-text-question__error-message">
        {{ c('answer.valid_characters') }}
      </div>
      <div v-for="field in textFields" :key="field.id" class="open-text-question-field__container">
        <label class="open-text-question-field">
          {{ field.title }}
          <textarea
            :class="{
              'open-text-question__text-area': true,
              'open-text-question__text-area--error': invalidAnswerIds.includes(field.id),
            }"
            :maxlength="question.characterLimit"
            :placeholder="c('open_text.textarea.placeholder')"
            :data-test-id="`OpenTextQuestion_TextInput-${field.id}`"
            autocomplete="off"
            @paste="
              (e) => {
                e.preventDefault()
              }
            "
            @drop="
              (e) => {
                e.preventDefault()
              }
            "
            @input="
              (e) =>
                setAnswer({
                  fieldId: field.id,
                  value: { textInput: (e.target as HTMLInputElement).value },
                })
            "
          />
          <p
            class="open-text-question__counter"
            :data-test-id="`OpenTextQuestion_CharacterCounter-${field.id}`"
          >
            {{ characterRemainings[field.id] }}
          </p>
        </label>
        <p v-if="invalidAnswerIds.includes(field.id)" class="open-text-question__error">
          {{ c('error.invalid_answer') }}
        </p>
      </div>
    </ScrollIntersectionObserver>
  </QuestionPadding>
</template>
<script lang="ts">
import { computed, type DeepReadonly, defineComponent, type PropType } from 'vue'

import { type Answer } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import QuestionPadding from '../../../question/components/dumb/QuestionPadding.vue'
import { type Question } from '../../../question/models/Question'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'

export default defineComponent({
  components: {
    QuestionPadding,
    ScrollIntersectionObserver,
    HintText,
  },
  props: {
    invalidAnswerIds: {
      type: Array as PropType<string[]>,
      required: true,
    },
    question: {
      type: Object as PropType<
        DeepReadonly<Pick<Question<'openText'>, 'fields' | 'characterLimit'>>
      >,
      required: true,
    },
    setAnswer: {
      type: Function as PropType<
        ({ fieldId, value }: { fieldId: string; value: Answer['value'] }) => void
      >,
      required: true,
    },
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
  },
  setup(props) {
    const textFields = computed(() =>
      props.question.fields.flatMap((field) => (field.type === 'text' ? [field] : [])),
    )
    const textAnswers = computed(() =>
      props.answers.flatMap((answer) => (answer.type === 'text' ? [answer] : [])),
    )
    const characterRemainings = computed(() => {
      return Object.fromEntries(
        textAnswers.value.map((answer) => [
          answer.fieldId,
          props.question.characterLimit - answer.value.textInput.length,
        ]),
      )
    })
    return { c, textFields, characterRemainings }
  },
})
</script>
<style lang="postcss">
.open-text-question {
  &-field__container {
    margin-bottom: 1rem;
  }
  &-field {
    display: block;
    margin-top: 0.4rem;
    position: relative;
  }
  &__text-area {
    font-weight: 400;
    color: #036;
    width: 100%;
    height: 100px;
    min-height: 5rem;
    padding: 1em;
    border: 1px solid var(--color-blue-grey);
    border-radius: 3px;
    resize: none;
    margin-top: 0.5rem;

    &--error {
      border: 1px solid #eb1325;
    }
  }
  &__error {
    margin-top: 4px;
    color: #eb1325;
  }
  &__counter {
    position: absolute;
    right: 1.14em;
    bottom: 1.14em;
  }
  &__error-message {
    color: #eb1325;
    background-color: #ffd6d5;
    padding: 9px;
    margin-bottom: 9px;
    text-align: center;
    line-height: 1.5rem;
  }
}
</style>
